import { useQuery } from '@tanstack/react-query'
import { ColumnDef } from '@tanstack/react-table'
import to from 'await-to-js'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import {
  DataTable,
  DataTableProvider,
  useDataTableContext,
} from 'components/DataTable'
import { IColumnVisibility } from 'components/DataTable/types'
import FbButton from 'components/FbUI/FbButton'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import ContactExportModal from 'components/Modals/ExportModal/ContactExportModal'
import dialogConfirm from 'components/dialogConfirm'
import { ContactResponse } from 'models/contacts'
import React, { useEffect, useMemo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { BsDownload, BsTags } from 'react-icons/bs'
import { toast } from 'react-toastify'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import styled from 'styled-components'
import { FilterIdentifier } from '../../../models/saved_view'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import { ActionButtons } from '../../Buttons/ActionButtons'
import { dataTableSortingStoreRepo } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import { DataTableContainer } from '../../DataTable/UI'
import { TableSearch } from '../../Filters/TableSearch'
import EditContactModal from '../../Modals/EditContactModal'
import * as S from '../CommonTable.styles'
import ContactsFilterset from './ContactsFilterset'
import { useContactsTableColumns } from './useContactsTableColumns'
import { SEQUENCE_SORTABLE_FIELDS } from './sequence/sequenceColumnInfo'
import { BiMailSend } from 'react-icons/bi'
import SendEmailModal from 'components/Modals/SendEmailModal'
import { SiSalesforce } from 'react-icons/si'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { ContactCompany } from 'models/companies'
const tooltip = <Tooltip id="tooltip">Download limited to 10,000 rows</Tooltip>

type SortableFields = (keyof ContactResponse | string)[]

export type ContactsTableRef = {
  refetch: () => void
}

const BASE_SORTABLE_FIELDS: SortableFields = [
  'email',
  'id',
  'contact_company',
  'name',
  'title',
  'type',
  'url',
  'sales_stage',
  'account_owners',
  'contact_labels',
  'company_tags',
  'phone_number',
  'deal_count',
  'sales_stages',
  'company_size',
  'company_cuisine_50',
  'created',
  'modified',
  'date_last_sent',
  'date_last_received',
]

const PAGE_SIZE = 100

interface ContactsTableProps {
  setTotalRowsCount?: (count: number) => void
  handleContactUpdated?: () => void
  forCompany?: Partial<ContactCompany>
  disableUrlPagination?: boolean
  disableExportButton?: boolean
  tableKey: string
  filterIdentifierModifier?: string
  baseFilters?: Record<string, any>
  forDoorProxyId?: number
  campaignId?: number
}

function ContactsTableComponent(props: ContactsTableProps) {
  const api = useApiService()
  const IDENTIFIER = 'CONTACTS_TABLE' + (props.filterIdentifierModifier ?? '')
  const contactsFilterStore = filterStoreRepo.getStore(
    IDENTIFIER as FilterIdentifier
  )
  const { page, setPage } = contactsFilterStore()

  const { sorting } = dataTableSortingStoreRepo.getStore(props.tableKey)()

  const {
    state: { totalSelectedRows },
    methods: { clearSelectedRows, setTotalRowsInBackend },
  } = useDataTableContext()
  const featureFlag = useFeatureFlagService()
  const campaignMode = !!props.campaignId && featureFlag.enableEmailSequencing
  const sortableFields = useMemo(
    () =>
      campaignMode
        ? [...BASE_SORTABLE_FIELDS, ...SEQUENCE_SORTABLE_FIELDS]
        : BASE_SORTABLE_FIELDS,
    [campaignMode]
  )
  const [showExportModal, setShowExportModal] = useState(false)
  const [showAssignContactTags, setShowAssignContactTags] = useState(false)
  const [showAssignCompanyTags, setShowAssignCompanyTags] = useState(false)
  const [showEditContactModal, setShowEditContactModal] =
    React.useState<ContactResponse | null>(null)
  const [showWriteEmailForContactModal, setShowWriteEmailForContactModal] =
    React.useState<ContactResponse | null>(null)

  const filtersAsParams = {
    ...useFilterParams(contactsFilterStore),
    ...props.baseFilters,
  }

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}

    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }

    return params
  }, [sorting])

  const filterAndSortParams = useMemo(
    () => ({
      ...filtersAsParams,
      ...sortParams,
      company: props.forCompany?.id,
      chain: props.forCompany?.chain,
      door_proxy_id: props.forDoorProxyId,
      ...(campaignMode ? { include_sequence_data: 'true' } : {}),
    }),
    [
      filtersAsParams,
      sortParams,
      props.forCompany,
      props.forDoorProxyId,
      campaignMode,
    ]
  )

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey: props.tableKey,
    filterParams: filterAndSortParams,
    page: page,
  })

  // DATA FETCHING
  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      clearSelectedRows()

      const body = {
        ...filterAndSortParams,
        page: page,
        limit: PAGE_SIZE,
      }

      return await api.getContactsList(body, signal)
    },
  })
  // END DATA FETCHING

  const columns = [
    ...(useContactsTableColumns(campaignMode) as any),
    {
      id: 'actions',
      header: 'Actions',
      size: 120,
      meta: {
        isEditable: false,
        headerDisableOrdering: true,
        isLastColumn: true,
        rightAlign: true,
      },
      cell: (info) => {
        const contact = info.row.original
        const contactId = contact.id
        const name = `${contact.first_name} ${contact.last_name}`

        return (
          <ActionButtons
            className={'justify-start min-w-28'}
            onDelete={() => handleContactDelete(contactId, name)}
            onEdit={() => setShowEditContactModal(contact)}
            append={
              <BiMailSend
                size={20}
                onClick={() => {
                  setShowWriteEmailForContactModal(contact)
                }}
                className="cursor-pointer text-gray-500"
              />
            }
          />
        )
      },
    },
  ] satisfies ColumnDef<ContactResponse, unknown>[]

  const handleContactDelete = async (contactId: number, name: string) => {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete <b>{name}</b>? This action cannot be
          undone.
        </>,
        'Delete Contact?',
        '',
        { confirmLabel: 'Delete' }
      )
    ) {
      const [err] = await to(api.deleteContact(contactId))
      if (!err) {
        toast.success('Contact deleted successfully')
        refetch()
      }
    }
  }

  useEffect(() => {
    clearSelectedRows()
    if (data?.count) {
      props.setTotalRowsCount?.(data.count)
      setTotalRowsInBackend(data.count)
    }
  }, [data])

  return (
    <DataTableContainer>
      <TagAssignModal
        show={showAssignContactTags}
        tagParent="contacts"
        handleClose={() => setShowAssignContactTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="tags"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
            isContactTags: true,
          })
        }}
        idAccessor="id"
      />

      <TagAssignModal
        show={showAssignCompanyTags}
        tagParent="products"
        handleClose={() => setShowAssignCompanyTags(false)}
        tableQueryKey={TABLE_QUERY_KEY}
        data={data?.results ?? []}
        tableObjAccessor="contact_company.taglist"
        filterAndSortParams={filterAndSortParams}
        apiTagsAction={(values) => {
          return api.commonManageTags({
            values,
            tableType: 'contacts-table',
          })
        }}
        idAccessor="contact_company.id"
      />

      {showEditContactModal && (
        <EditContactModal
          show={!!showEditContactModal}
          handleClose={() => setShowEditContactModal(null)}
          onContactUpdated={props.handleContactUpdated}
          contact={showEditContactModal}
          forCompany={props.forCompany}
          initialDoorIds={props.forDoorProxyId ? [props.forDoorProxyId] : []}
        />
      )}

      {showWriteEmailForContactModal && (
        <SendEmailModal
          show={!!showWriteEmailForContactModal}
          handleClose={() => setShowWriteEmailForContactModal(null)}
          contact={showWriteEmailForContactModal}
        />
      )}

      <ContactExportModal
        tableTypeSlug="contacts-table"
        count={data?.count ?? 0}
        show={showExportModal}
        _handleClose={() => setShowExportModal(false)}
        filters={filterAndSortParams}
      />

      <DataTable
        key={props.tableKey}
        tableHeader={
          <S.SearchContainer>
            <S.SearchContainerFilters>
              <S.TableSearchContainer>
                <TableSearch
                  filterStore={contactsFilterStore}
                  searchPlaceholder="Search by name, company, company domain, email..."
                  tableKey={props.tableKey}
                />
              </S.TableSearchContainer>
              <S.TableButtonsContainer>
                <ContactsFilterset
                  filterIdentifier={IDENTIFIER as FilterIdentifier}
                  campaignMode={campaignMode}
                />
                <ColumnSelectorRecipient tableKey={props.tableKey} />
                <TableButtonAction
                  items={[
                    {
                      label: 'Manage Contact Labels',
                      icon: <BsTags size={20} />,
                      callback: () => setShowAssignContactTags(true),
                    },
                    {
                      label: 'Manage Company Tags',
                      icon: <BsTags size={20} />,
                      callback: () => setShowAssignCompanyTags(true),
                    },
                    {
                      label: 'Push to Salesforce',
                      icon: <SiSalesforce size={20} />,
                      hidden: !featureFlag.enablePushToSalesforce,
                      callback: () => {
                        setTimeout(() => {
                          toast.success('Successfully pushed to Salesforce')
                        }, 1000)
                      },
                    },
                  ]}
                  disabled={!totalSelectedRows}
                  selectedRowsCount={totalSelectedRows ?? 0}
                />
              </S.TableButtonsContainer>
            </S.SearchContainerFilters>
            <FilterChips
              identifier={'CONTACTS_TABLE'}
              store={contactsFilterStore}
              clearAllButton
              showActive
            />
          </S.SearchContainer>
        }
        tableKey={props.tableKey}
        loading={isFetching}
        data={data?.results ?? []}
        columns={columns}
        sortableFields={sortableFields}
        enableRowSelection
        virtualizeRows={false}
        isPaginationEnabled={true}
        stickyLastColumn
        paginationOptions={{
          totalRows: data?.count ?? 0,
          pageSize: PAGE_SIZE,
          setPage: setPage,
          page: page,
          isPaginationLoading: isFetching,
        }}
        selectAllText={'Select Contacts'}
        footerControls={
          !props.disableExportButton ? (
            <OverlayTrigger placement="top" overlay={tooltip}>
              <ExportButton
                variant="secondary"
                onClick={() => setShowExportModal(true)}
              >
                <BsDownload size={18} />
              </ExportButton>
            </OverlayTrigger>
          ) : undefined
        }
      />
    </DataTableContainer>
  )
}

ContactsTableComponent.displayName = 'ContactsTable'

export function ContactsTable(
  props: ContactsTableProps & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, campaignId, ...rest } = props

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      defaultColumnVisibility={defaultColumnVisibility}
      preferredGroupOrder={[
        'Contact Information',
        'Email Sequence',
        'Activity',
        'Account Details',
      ]}
    >
      <DataTableProvider tableKey={tableKey}>
        <ContactsTableComponent
          tableKey={tableKey}
          campaignId={campaignId}
          {...rest}
        />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}

const ExportButton = styled(FbButton)`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 8px 14px;

  gap: 8px;

  height: 44px;

  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
`
